import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue'

import type { PjazzaTabLine } from '@/domains/pjazza/types/models/PjazzaTab.types.ts'
import type { PjazzaPhone } from '@/domains/pjazza/utils/phoneNumber.ts'
import type { ApiErrorShape } from '@/utils/api-error/apiError'

import { callOrderingOrders, callOrderingVerification } from '@/domains/pjazza/services'
import { useBasketStore } from '@/domains/pjazza/store/use-basket-store/useBasketStore.ts'
import { usePjazzaAuthStore } from '@/domains/pjazza/store/use-pjazza-auth-store/usePjazzaAuthStore.ts'
import { HTTP_STATUS } from '@/domains/pjazza/types/consts/HttpStatus.consts.ts'
import { orderStatusUrl } from '@/domains/pjazza/utils/orderStatusUrl.ts'
import { isApiError } from '@/utils/api-error/apiError'

const RESEND_COOLDOWN_SECONDS = 60

/** What the phone step reads before the parser has arrived: a number nobody can send yet. */
const UNPARSED_PHONE: PjazzaPhone = { isValid: false, e164: null, formatted: null }

interface PlacedOrder {
  statusUrl: string
  lines: PjazzaTabLine[]
  /** What the order-sent sheet polls. Null on a payment page: no order exists yet. */
  order: { id: string; statusToken: string } | null
  /**
   * True when `statusUrl` is the venue's payment page rather than one of ours.
   *
   * The caller has to leave the app for it — an Inertia visit cannot reach
   * another origin — and must not count the order as live, because at a paying
   * venue no order exists until the money lands.
   */
  isPayment: boolean
}

/**
 * Drives takeaway checkout: verify a phone, then place the order.
 *
 * The two steps are deliberately sequential rather than one submit — the
 * venue needs a reachable number to call about a problem order, and the code
 * exchange is also what mints the token that authorises placement. A guest
 * never gets an account or a password out of it.
 */
export const useCheckoutFlow = (siteSlug: string) => {
  const basket = useBasketStore()

  const auth = usePjazzaAuthStore()

  const step = ref<'details' | 'verify'>('details')
  const name = ref('')
  const phone = ref('')
  const code = ref('')
  const notes = ref('')
  /** Optional: a phone is enough to collect an order; email only buys a receipt. */
  const email = ref('')

  const isSending = ref(false)
  const isVerifying = ref(false)
  const errorMessage = ref<string | null>(null)
  const resendAvailableIn = ref(0)

  let cooldownTimer: ReturnType<typeof setInterval> | null = null

  // libphonenumber and its metadata are the heaviest thing this composable
  // carries, and only the phone step reads them. The venue page shares the
  // composable for placement alone and never shows that step, so the parser
  // arrives with the first digit typed rather than with the page.
  const parsePhone = shallowRef<((raw: string) => PjazzaPhone) | null>(null)

  watch(
    phone,
    value => {
      if (!value || parsePhone.value) return

      void import('@/domains/pjazza/utils/phoneNumber.ts').then(module => {
        parsePhone.value = module.parsePjazzaPhone
      })
    },
    { immediate: true }
  )

  // The same reading the sign-in sheet uses — one implementation, so the two
  // cannot disagree about whether a number is acceptable.
  const parsedPhone = computed(() => parsePhone.value?.(phone.value) ?? UNPARSED_PHONE)

  const isPhoneValid = computed(() => parsedPhone.value.isValid)

  /**
   * Somebody who signed in on the way here does not verify again.
   *
   * The cart sheet now gates on sign-in before it hands over to checkout, so
   * this is the common path; the phone step survives for anyone who lands on
   * checkout directly with no session.
   */
  const needsVerification = computed(() => !auth.isSignedIn)

  const canSendCode = computed(() => name.value.trim().length > 1 && isPhoneValid.value && !isSending.value)

  const canPlaceDirectly = computed(() => !needsVerification.value && !isVerifying.value)

  const canVerify = computed(() => code.value.trim().length === 6 && !isVerifying.value)

  const startCooldown = (): void => {
    resendAvailableIn.value = RESEND_COOLDOWN_SECONDS

    if (cooldownTimer) clearInterval(cooldownTimer)

    cooldownTimer = setInterval(() => {
      resendAvailableIn.value = Math.max(0, resendAvailableIn.value - 1)

      if (resendAvailableIn.value === 0 && cooldownTimer) clearInterval(cooldownTimer)
    }, 1000)
  }

  /*
   * A diner who asks for a new code and then leaves the page mid-cooldown left
   * the interval running, ticking a ref nothing was watching any more. Bounded
   * at the 60 seconds it takes to reach zero, so it never grew — but it is a
   * timer outliving its scope, and Inertia navigation makes that routine.
   */
  onScopeDispose(() => {
    if (cooldownTimer) clearInterval(cooldownTimer)
  })

  const messageFrom = (error: unknown, fallback: string): string => {
    const err: ApiErrorShape = isApiError(error) ? error : {}
    const isUnauth = err.status === HTTP_STATUS.UNAUTHORIZED || err.data?.message === 'Unauthenticated.'

    if (isUnauth) {
      auth.signOut()

      return 'Your session has expired. Please sign in again.'
    }

    // A rapid run of retries trips the placement throttle. The order is fine —
    // it just needs a breath — so say that rather than the generic failure,
    // which reads as "it broke" and invites another immediate tap.
    if (err.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
      return 'One moment — give it a few seconds, then try again.'
    }

    return err.data?.message ?? fallback
  }

  const sendCode = async (): Promise<void> => {
    if (!canSendCode.value) return

    isSending.value = true
    errorMessage.value = null

    try {
      await callOrderingVerification().request({
        siteSlug,
        phone: parsedPhone.value.e164 ?? phone.value
      })

      step.value = 'verify'
      startCooldown()
    } catch (error) {
      errorMessage.value = messageFrom(error, "We couldn't send your code. Try again in a moment.")
    } finally {
      isSending.value = false
    }
  }

  /**
   * Places the order with the session already held.
   *
   * Returns the placed order — the status URL to send them to and its priced
   * lines, so the confirmation can show the whole bill without a second fetch.
   */
  const place = async (): Promise<PlacedOrder> => {
    const placed = await callOrderingOrders()
      .placeTakeaway({
        siteSlug,
        items: basket.toPayload(),
        notes: notes.value.trim() || null,
        name: name.value.trim() || null,
        email: email.value.trim() || null,
        dueAt: null,
        idempotencyKey: basket.idempotencyKey
      })
      .catch((error: unknown) => {
        // A 409 refuses this key for good, so the next attempt needs its own.
        if (isApiError(error) && error.status === HTTP_STATUS.CONFLICT) basket.renewKey()

        throw error
      })

    const body = placed.data.value?.data

    if (!body) throw new Error('No order returned')

    /*
     * A paying venue answers with a payment page instead of an order.
     *
     * The basket is deliberately NOT cleared here: no order exists yet, so a
     * diner who abandons the payment page would otherwise come back to an
     * empty basket and have to rebuild it. Resubmitting the same basket reuses
     * the same idempotency key, which returns the checkout already opened
     * rather than charging twice; changing the basket changes the key.
     *
     * It is cleared when the diner lands back on a real order — the payment
     * return sends them to OrderStatus/Show with `justPaid`, and that page does
     * it. Nothing here can, because the browser leaves for another origin.
     */
    if ('checkout' in body) {
      const hostedUrl = body.checkout.hostedUrl

      if (!hostedUrl) throw new Error('No payment page returned')

      return { statusUrl: hostedUrl, lines: [], order: null, isPayment: true }
    }

    basket.clear()

    return {
      statusUrl: orderStatusUrl({ siteSlug, orderId: body.order.id, statusToken: body.statusToken }),
      lines: body.order.lines,
      order: { id: body.order.id, statusToken: body.statusToken },
      isPayment: false
    }
  }

  /** For a diner who already holds a session — no code to exchange. */
  const placeNow = async (): Promise<PlacedOrder | null> => {
    if (!canPlaceDirectly.value) return null

    isVerifying.value = true
    errorMessage.value = null

    try {
      return await place()
    } catch (error) {
      errorMessage.value = messageFrom(error, "That didn't reach the kitchen. Try again in a moment.")

      return null
    } finally {
      isVerifying.value = false
    }
  }

  /**
   * Verifies the code and places the order in one action, because a verified
   * session with no order is a dead end for the diner.
   *
   * Returns the placed order (status URL + priced lines), or null if it failed.
   */
  const verifyAndPlace = async (): Promise<PlacedOrder | null> => {
    if (!canVerify.value) return null

    isVerifying.value = true
    errorMessage.value = null

    try {
      const session = await callOrderingVerification().verify({
        siteSlug,
        phone: parsedPhone.value.e164 ?? phone.value,
        code: code.value.trim(),
        deviceName: 'ordering-web'
      })

      const token = session.data.value?.data.token

      if (!token) throw new Error('No session token returned')

      // The grouped form: the store's phone is a label for the account menu, not
      // an identifier, and "+356 7912 3456" is what the diner recognises.
      auth.signIn({
        token,
        expiresAt: session.data.value?.data.expiresAt ?? null,
        phone: parsedPhone.value.formatted ?? phone.value,
        name: session.data.value?.data.name ?? null,
        avatarUrl: session.data.value?.data.avatarUrl ?? null
      })

      return await place()
    } catch (error) {
      errorMessage.value = messageFrom(error, "That code didn't work. Check it and try again.")

      return null
    } finally {
      isVerifying.value = false
    }
  }

  const backToDetails = (): void => {
    step.value = 'details'
    code.value = ''
    errorMessage.value = null
  }

  return {
    step,
    name,
    email,
    phone,
    code,
    notes,
    isSending,
    isVerifying,
    errorMessage,
    resendAvailableIn,
    isPhoneValid,
    needsVerification,
    canSendCode,
    canPlaceDirectly,
    canVerify,
    sendCode,
    placeNow,
    verifyAndPlace,
    backToDetails
  }
}
